import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import {
  ShippingContainer, Plus, Truck, Package, Barcode, Warning, Check,
  ClipboardText, FileText, Eye, X, CaretRight, ListChecks, CircleNotch,
  Lock, Prohibit, Pause, PaperPlaneTilt, ArrowUUpLeft, Trash, DownloadSimple,
  Camera, ImageSquare, Printer, Lightning, Gauge, CalendarCheck, Truck as TruckIcon,
  ShieldCheck, ArrowsCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import BarcodeScanner from '../../components/BarcodeScanner';
import PalletQuickLogDialog from '../../components/pallets/PalletQuickLogDialog';
import {
  getDispatchDashboard, getDispatches, getDispatch, createDispatch, updateDispatch,
  transitionDispatch, allocateDispatchOrder, deallocateDispatchOrder, loadScanDispatch,
  sealDispatch, cancelDispatch, holdDispatch, uploadDispatchDoc, listDispatchDocs,
  getDispatchDoc, deleteDispatchDoc, getDispatchAudit, getOrders,
  addDispatchPhoto, listDispatchPhotos, deleteDispatchPhoto, getDispatchOptimise,
  listDocks, bookDock, releaseDock, bookManualShipping, removeManualShipping,
  dispatchManifestPdfUrl, dispatchBolPdfUrl, getDispatchLoadSequence,
  markDispatchDelivered, markDispatchReturned, getDocksHeatmap,
  heatmapAutoResolve, generateCarrierHandoff, listCarrierHandoffs, revokeCarrierHandoff,
  wsEventsUrl, listFreeDocks,
} from '../../lib/api';


// Extracted helpers, constants, and tab sub-components
import {
  CONTAINER_TYPES, LOAD_TYPES, STATUS_META, NEXT_STATUS,
  DashCard, SummaryBar, SumItem,
} from './_shared';
import { DetailsTab } from './tabs/DetailsTab';
import { AllocationsTab } from './tabs/AllocationsTab';
import { StagingTab } from './tabs/PickingTab';
import { LoadingTab } from './tabs/LoadingTab';
import { DocsAuditTab } from './tabs/DocumentsTab';
import {
  CreateDialog, AllocateDialog, SealDialog, DocsDialog,
  PhotoCaptureDialog, PhotoGalleryDialog, OptimiserDialog,
  DockBookingDialog, ManualShippingDialog,
  DockHeatmapDialog, CarrierHandoffDialog,
} from './dialogs';

export default function Dispatch() {
  const { user } = useAuth();
  const isManager = ['admin', 'manager'].includes(user?.role);

  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('details');
  const [scanOpen, setScanOpen] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [docs, setDocs] = useState([]);
  // Phase 2a state
  const [photoOpen, setPhotoOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [optimiseOpen, setOptimiseOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);
  const [shippingOpen, setShippingOpen] = useState(false);
  // Phase 3
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [palletLogOpen, setPalletLogOpen] = useState(false);
  // Phase 3h — Driver-arriving live overlay (dispatch_id → {driver_name, minutes_until, seen_at})
  const [arriving, setArriving] = useState({});
  // Phase 3i — Quick-assign-dock on arrival
  const [quickAssign, setQuickAssign] = useState(null); // {dispatchId, dispatchNumber}

  const loadList = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [d, l] = await Promise.all([getDispatchDashboard(), getDispatches(params)]);
      setDashboard(d.data);
      setRows(l.data || []);
      // Seed arriving map from server-side flag
      const seed = {};
      (l.data || []).forEach(row => {
        if (row.driver_arriving) {
          seed[row.id] = {
            driver_name: row.driver_arriving.driver_name,
            minutes_until: row.driver_arriving.minutes_until,
            seen_at: row.driver_arriving.last_seen_at,
          };
        }
      });
      setArriving(seed);
    } catch {
      toast.error('Failed to load dispatches');
    }
  }, [statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  // WebSocket subscription for live driver-arriving events
  useEffect(() => {
    let ws, reconnect;
    const connect = () => {
      try {
        ws = new WebSocket(wsEventsUrl());
        ws.onmessage = (ev) => {
          try {
            const evt = JSON.parse(ev.data);
            if (evt.type !== 'dispatch.driver_arriving') return;
            setArriving(prev => ({
              ...prev,
              [evt.dispatch_id]: {
                driver_name: evt.driver_name,
                driver_phone: evt.driver_phone,
                minutes_until: evt.minutes_until,
                seen_at: evt.last_seen_at || new Date().toISOString(),
              },
            }));
          } catch { /* ignore */ }
        };
        ws.onclose = () => { reconnect = setTimeout(connect, 5000); };
        ws.onerror = () => { /* let close reconnect */ };
      } catch { /* ignore */ }
    };
    connect();
    return () => { if (reconnect) clearTimeout(reconnect); if (ws) ws.close(); };
  }, []);

  // Auto-expire stale arriving badges every 30s (2-min freshness)
  useEffect(() => {
    const t = setInterval(() => {
      setArriving(prev => {
        const cutoff = Date.now() - 2 * 60 * 1000;
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          if (v.seen_at && new Date(v.seen_at).getTime() >= cutoff) next[k] = v;
        });
        return next;
      });
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  const openDetail = async (id) => {
    setSelectedId(id);
    setTab('details');
    try {
      const { data } = await getDispatch(id);
      setDetail(data);
    } catch {
      toast.error('Failed to load dispatch');
    }
  };

  const refreshDetail = async () => {
    if (!selectedId) return;
    const { data } = await getDispatch(selectedId);
    setDetail(data);
    await loadList();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="dispatch-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              <ShippingContainer size={28} weight="fill" className="inline text-brand mr-2" />
              Outbound Dispatch
            </h1>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Booking → Allocation → Picking → Loading → Seal → Dispatch
            </p>
          </div>
          {isManager && (
            <div className="flex gap-2">
              <Button
                onClick={() => setHeatmapOpen(true)}
                data-testid="open-dock-heatmap-btn"
                variant="outline"
                className="rounded-none border-border hover:border-brand hover:text-brand"
              >
                <Gauge size={16} className="mr-1" weight="bold" /> Dock Heatmap
              </Button>
              <Button
                onClick={() => setCreateOpen(true)}
                data-testid="create-dispatch-btn"
                className="bg-brand text-white rounded-none hover:bg-[#0040d9]"
              >
                <Plus size={16} className="mr-1" weight="bold" /> New Dispatch
              </Button>
            </div>
          )}
        </div>

        {/* Dashboard widgets */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <DashCard label="Due today" value={dashboard.due_today} icon={Truck} />
            <DashCard label="Loading now" value={dashboard.loading_now} icon={Package} accent="amber" />
            <DashCard label="Dispatched today" value={dashboard.dispatched_today} icon={Check} accent="emerald" />
            <DashCard label="On hold" value={dashboard.on_hold} icon={Pause} accent="orange" />
            <DashCard label="Planned" value={dashboard.planned} icon={ClipboardText} />
            <DashCard label="Sealed pending" value={dashboard.sealed_pending} icon={Lock} accent="blue" />
            <DashCard label="Delayed" value={dashboard.delayed} icon={Warning} accent={dashboard.delayed > 0 ? 'rose' : 'slate'} />
          </div>
        )}

        {/* Filter + list */}
        <div className="bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Filter:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter"
              className="border border-border px-2 py-1 text-sm rounded-none font-mono"
            >
              <option value="all">All</option>
              {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
            <span className="ml-auto text-xs text-muted-foreground font-mono">{rows.length} record(s)</span>
          </div>
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Dispatch #</th>
                <th>Customer</th>
                <th>Container</th>
                <th>Type</th>
                <th>Planned</th>
                <th>Status</th>
                <th className="text-right">Util.</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const sm = r.summary || {};
                const util = Math.max(sm.weight_pct || 0, sm.volume_pct || 0);
                const arr = arriving[r.id];
                return (
                  <tr key={r.id}
                    data-testid={`dispatch-row-${r.dispatch_number}`}
                    className={arr ? 'bg-amber-50 border-l-4 border-amber-500' : ''}
                  >
                    <td className="font-mono font-bold text-brand">
                      {r.dispatch_number}
                      {arr && (
                        <>
                          <span
                            data-testid={`driver-arriving-badge-${r.dispatch_number}`}
                            title={`${arr.driver_name || 'Driver'} viewing pickup portal${arr.driver_phone ? ` · ${arr.driver_phone}` : ''}`}
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white animate-pulse"
                          >
                            <Truck size={10} weight="fill" />
                            {arr.minutes_until > 0 ? `arriving ${arr.minutes_until}m` : arr.minutes_until < 0 ? `here ${Math.abs(arr.minutes_until)}m ago` : 'arriving now'}
                          </span>
                          {!r.dock_booking?.dock_code && isManager && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setQuickAssign({ dispatchId: r.id, dispatchNumber: r.dispatch_number }); }}
                              data-testid={`quick-assign-dock-${r.dispatch_number}`}
                              title="Assign a free dock for this driver"
                              className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-amber-600 bg-card text-amber-700 hover:bg-amber-600 hover:text-white transition"
                            >
                              Assign Dock
                            </button>
                          )}
                          {r.dock_booking?.dock_code && (
                            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-emerald-600 text-white"
                              data-testid={`assigned-dock-${r.dispatch_number}`}>
                              → {r.dock_booking.dock_code}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>{r.customer_name}</td>
                    <td className="font-mono">{r.container_number || <span className="text-muted-foreground">—</span>}</td>
                    <td className="text-xs">{r.container_type}</td>
                    <td className="text-xs font-mono">
                      {r.planned_dispatch_at ? new Date(r.planned_dispatch_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_META[r.status]?.color || 'bg-muted'}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="text-right text-xs font-mono">
                      {(sm.total_allocated_units || 0) > 0 ? `${util.toFixed(0)}%` : '—'}
                    </td>
                    <td>
                      <button
                        onClick={() => openDetail(r.id)}
                        data-testid={`open-${r.dispatch_number}`}
                        className="text-brand hover:underline text-xs font-bold"
                      >
                        Open <CaretRight size={11} className="inline" weight="bold" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No dispatches.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create dialog */}
        <CreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={async (id) => { setCreateOpen(false); await loadList(); await openDetail(id); }}
        />

        {/* Detail drawer */}
        <Dialog open={!!selectedId} onOpenChange={(o) => { if (!o) { setSelectedId(null); setDetail(null); } }}>
          <DialogContent className="border border-border rounded-none max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-detail-dialog">
            {detail && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-2xl flex items-center gap-2">
                        <ShippingContainer size={22} weight="fill" className="text-brand" />
                        {detail.dispatch_number}
                        <span className={`ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_META[detail.status]?.color}`}>
                          {STATUS_META[detail.status]?.label}
                        </span>
                      </DialogTitle>
                      <DialogDescription className="text-xs">
                        {detail.customer_name} · {detail.container_type} · {detail.destination || 'no destination'}
                      </DialogDescription>
                    </div>
                    {isManager && detail.status !== 'dispatched' && detail.status !== 'cancelled' && (
                      <div className="flex items-center gap-2">
                        {NEXT_STATUS[detail.status] && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await transitionDispatch(detail.id, NEXT_STATUS[detail.status]);
                                toast.success(`→ ${STATUS_META[NEXT_STATUS[detail.status]].label}`);
                                await refreshDetail();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Transition failed');
                              }
                            }}
                            data-testid="advance-status-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                          >
                            <PaperPlaneTilt size={12} weight="bold" className="mr-1" />
                            → {STATUS_META[NEXT_STATUS[detail.status]].label}
                          </Button>
                        )}
                        {detail.status !== 'on_hold' && (
                          <Button
                            size="sm" variant="outline"
                            onClick={async () => {
                              try { await holdDispatch(detail.id, 'Manager hold'); toast.success('On hold'); await refreshDetail(); }
                              catch (e) { toast.error(e?.response?.data?.detail || 'Hold failed'); }
                            }}
                            className="rounded-none border-orange-300 text-orange-700"
                          >
                            <Pause size={12} weight="bold" className="mr-1" /> Hold
                          </Button>
                        )}
                        <Button
                          size="sm" variant="outline"
                          onClick={async () => {
                            if (!window.confirm('Cancel this dispatch?')) return;
                            try { await cancelDispatch(detail.id, 'Manager cancel'); toast.success('Cancelled'); await refreshDetail(); }
                            catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
                          }}
                          className="rounded-none border-rose-300 text-rose-700"
                        >
                          <Prohibit size={12} weight="bold" className="mr-1" /> Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogHeader>

                {/* Summary bar */}
                <SummaryBar summary={detail.summary} />

                {/* Tabs */}
                <div className="flex border-b border-border mt-3">
                  {[
                    { k: 'details', label: 'Dispatch Details' },
                    { k: 'allocations', label: 'Orders & Allocations' },
                    { k: 'staging', label: 'Picking & Staging' },
                    { k: 'loading', label: 'Loading & Seal' },
                    { k: 'docs', label: 'Documents & Audit' },
                  ].map(t => (
                    <button
                      key={t.k}
                      onClick={() => setTab(t.k)}
                      data-testid={`tab-${t.k}`}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-2 ${tab === t.k ? 'border-brand text-brand' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {tab === 'details' && (
                  <DetailsTab
                    detail={detail}
                    isManager={isManager}
                    onSaved={refreshDetail}
                    onOpenDock={() => setDockOpen(true)}
                    onOpenShipping={() => setShippingOpen(true)}
                    onOpenHandoff={() => setHandoffOpen(true)}
                  />
                )}
                {tab === 'allocations' && (
                  <AllocationsTab
                    detail={detail}
                    isManager={isManager}
                    onOpenAllocate={() => setAllocateOpen(true)}
                    onOpenOptimise={() => setOptimiseOpen(true)}
                    onRefresh={refreshDetail}
                  />
                )}
                {tab === 'staging' && <StagingTab detail={detail} />}
                {tab === 'loading' && (
                  <LoadingTab
                    detail={detail}
                    isManager={isManager}
                    onOpenScan={() => setScanOpen(true)}
                    onOpenSeal={() => setSealOpen(true)}
                    onOpenPhoto={() => setPhotoOpen(true)}
                    onOpenGallery={async () => {
                      const { data } = await listDispatchPhotos(detail.id);
                      setGalleryPhotos(data || []);
                      setGalleryOpen(true);
                    }}
                    onOpenPalletLog={() => setPalletLogOpen(true)}
                  />
                )}
                {tab === 'docs' && (
                  <DocsAuditTab
                    detail={detail}
                    isManager={isManager}
                    docs={docs}
                    audit={audit}
                    onOpenDocs={async () => {
                      const { data } = await listDispatchDocs(detail.id);
                      setDocs(data || []);
                      setDocsOpen(true);
                    }}
                    onOpenAudit={async () => {
                      const { data } = await getDispatchAudit(detail.id);
                      setAudit(data.audit || []);
                      setAuditOpen(true);
                    }}
                  />
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Allocation dialog */}
        <AllocateDialog
          open={allocateOpen}
          onOpenChange={setAllocateOpen}
          dispatchId={detail?.id}
          existing={detail?.allocations || []}
          onAllocated={async () => { setAllocateOpen(false); await refreshDetail(); }}
        />

        {/* Scan-load dialog */}
        {scanOpen && (
          <BarcodeScanner
            title="Scan SKU / Carton to Load"
            onClose={() => setScanOpen(false)}
            onScan={async (code) => {
              try {
                const { data } = await loadScanDispatch(detail.id, { sku_code: code, qty: 1 });
                toast.success(`Loaded · ${code} · total ${data.qty_loaded_total}`);
                await refreshDetail();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Scan failed');
              }
            }}
          />
        )}

        {/* Seal dialog */}
        <SealDialog
          open={sealOpen}
          onOpenChange={setSealOpen}
          dispatchId={detail?.id}
          reefer={detail?.container_type?.includes('REEFER')}
          dgFlag={detail?.dg_flag}
          onSealed={async () => { setSealOpen(false); await refreshDetail(); }}
        />

        {/* Docs dialog */}
        <DocsDialog
          open={docsOpen}
          onOpenChange={setDocsOpen}
          dispatchId={detail?.id}
          docs={docs}
          onRefresh={async () => {
            const { data } = await listDispatchDocs(detail.id);
            setDocs(data || []);
            await refreshDetail();
          }}
          isManager={isManager}
        />

        {/* Phase 2a: Photo capture */}
        <PhotoCaptureDialog
          open={photoOpen}
          onOpenChange={setPhotoOpen}
          dispatchId={detail?.id}
          onUploaded={async () => { setPhotoOpen(false); await refreshDetail(); }}
        />
        {/* Phase 2a: Photo gallery */}
        <PhotoGalleryDialog
          open={galleryOpen}
          onOpenChange={setGalleryOpen}
          photos={galleryPhotos}
          dispatchId={detail?.id}
          isManager={isManager}
          onRefresh={async () => {
            const { data } = await listDispatchPhotos(detail.id);
            setGalleryPhotos(data || []);
          }}
        />
        {/* Phase 2a: Optimiser */}
        <OptimiserDialog
          open={optimiseOpen}
          onOpenChange={setOptimiseOpen}
          dispatchId={detail?.id}
          isManager={isManager}
          onAllocated={async () => { setOptimiseOpen(false); await refreshDetail(); }}
        />
        {/* Phase 2a: Dock booking */}
        <DockBookingDialog
          open={dockOpen}
          onOpenChange={setDockOpen}
          dispatchId={detail?.id}
          current={detail?.dock_booking}
          onSaved={async () => { setDockOpen(false); await refreshDetail(); }}
        />
        {/* Phase 2a: Manual shipping */}
        <ManualShippingDialog
          open={shippingOpen}
          onOpenChange={setShippingOpen}
          dispatchId={detail?.id}
          current={detail?.manual_shipping}
          onSaved={async () => { setShippingOpen(false); await refreshDetail(); }}
        />
        {/* Phase 3c: Carrier Portal Handoff */}
        <CarrierHandoffDialog
          open={handoffOpen}
          onOpenChange={setHandoffOpen}
          dispatchId={detail?.id}
          dispatchNumber={detail?.dispatch_number}
        />
        {/* Phase 3i: Quick dock assign on driver arrival */}
        <QuickDockAssignDialog
          assign={quickAssign}
          onOpenChange={() => setQuickAssign(null)}
          onAssigned={async () => { setQuickAssign(null); await loadList(); }}
        />
        {/* Phase 3: Dock Heatmap */}
        <DockHeatmapDialog
          open={heatmapOpen}
          onOpenChange={setHeatmapOpen}
        />

        {/* Audit dialog */}
        <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
          <DialogContent className="border border-border rounded-none max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Audit Trail · {detail?.dispatch_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-2" data-testid="audit-list">
              {(audit || []).map((a, i) => (
                <div key={i} className="border-l-2 border-brand pl-3 py-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-foreground font-mono uppercase">{a.action}</span>
                    <span className="text-muted-foreground font-mono">{new Date(a.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {a.actor_name || a.actor_id}{' '}
                    {a.details && Object.keys(a.details).length > 0 && (
                      <span className="text-muted-foreground font-mono">· {JSON.stringify(a.details)}</span>
                    )}
                  </p>
                </div>
              ))}
              {(audit || []).length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No audit entries.</p>}
            </div>
          </DialogContent>
        </Dialog>
        <PalletQuickLogDialog
          open={palletLogOpen}
          setOpen={setPalletLogOpen}
          source="despatch"
          reference={detail?.dispatch_number}
          merchantId={detail?.merchant_id}
          warehouseId={detail?.warehouse_id}
          onLogged={async () => { setPalletLogOpen(false); await refreshDetail(); }}
        />
      </div>
    </Layout>
  );
}

// ============== SUBCOMPONENTS ==============


// ---- Dialogs have been extracted to ./dialogs.jsx ----

// ---------- Phase 3i: Quick dock-assign on driver arrival ----------
// Tiny inline dialog opened from the amber "Assign Dock" button on the dispatch
// list. Fetches free docks in the next 30 min and books on one click.
const QuickDockAssignDialog = ({ assign, onOpenChange, onAssigned }) => {
  const [docks, setDocks] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = !!assign;

  useEffect(() => {
    if (!open) { setDocks(null); return; }
    (async () => {
      try {
        const r = await listFreeDocks(30);
        setDocks(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load docks');
      }
    })();
  }, [open]);

  const assignDock = async (dockCode) => {
    setBusy(true);
    try {
      const start = new Date(); // now
      await bookDock(assign.dispatchId, {
        dock_code: dockCode,
        start_at: start.toISOString(),
        duration_minutes: 30,
      });
      toast.success(`Dock ${dockCode} assigned · driver notified`);
      if (onAssigned) onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onOpenChange(); }}>
      <DialogContent className="border border-border rounded-none max-w-lg" data-testid="quick-dock-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-amber-500" />
            Assign dock · {assign?.dispatchNumber}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Free bays in the next 30 min — tap any to book immediately. Driver's PWA gets a push + dashboard update.
          </DialogDescription>
        </DialogHeader>

        {!docks && (
          <p className="py-6 text-center text-muted-foreground text-sm">Loading free docks…</p>
        )}
        {docks && (
          <div className="space-y-2 mt-2">
            <p className="text-[11px] text-muted-foreground font-mono">
              {docks.free_count} of {(docks.docks || []).length} docks free · window {new Date(docks.window_start).toLocaleTimeString()}–{new Date(docks.window_end).toLocaleTimeString()}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(docks.docks || []).map(d => (
                <button
                  key={`${d.warehouse_id || 'default'}-${d.dock_code}`}
                  type="button"
                  onClick={() => d.free && assignDock(d.dock_code)}
                  disabled={!d.free || busy}
                  data-testid={`quick-dock-${d.dock_code}`}
                  title={d.free
                    ? `Click to book ${d.dock_code} for 30 min`
                    : `Busy · ${(d.conflicting_bookings || []).map(b => b.dispatch_number).join(', ')}`}
                  className={`flex items-center justify-between px-3 py-2 border text-left transition ${
                    d.free
                      ? 'border-emerald-400 bg-emerald-50 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 cursor-pointer'
                      : 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  <div>
                    <div className="font-mono font-bold text-sm">{d.dock_code}</div>
                    {!d.free && (
                      <div className="text-[10px] font-mono mt-0.5">
                        busy · {(d.conflicting_bookings || [])[0]?.dispatch_number || '—'}
                      </div>
                    )}
                  </div>
                  {d.free && <span className="text-[10px] font-bold uppercase tracking-widest">Free</span>}
                </button>
              ))}
            </div>
            {docks.free_count === 0 && (
              <p className="text-xs text-rose-600 pt-2" data-testid="quick-dock-none">
                All docks are busy in the next 30 min. Use the Dock Heatmap to find the nearest free slot.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

